import React, { useState } from "react";
import { Button, Form, Icon } from "semantic-ui-react";
import { Card, Disclosure, EvidenceDisclosure, Expander, LinkOut, SourceLinks, StatusBadge, THEME_ICONS, TopicCard } from "./PiEvidence";

// Report tabs. Each renders the server-built presentation model `p` (see
// functions/propertyIntelligence/presentation.js); nothing here re-derives
// evidence or contacts a research provider.

const TAB_TITLES = { details: "Property Details", history: "History & Permits", hoaMetro: "HOA & Metro", risks: "Risks & Insurance", neighborhood: "Neighborhood" };

// ---------------------------------------------------------------- Overview

const GLANCE_ICONS = {
  propertyType: "home",
  yearBuilt: "calendar alternate outline",
  bedrooms: "bed",
  bathrooms: "bath",
  aboveGradeSqft: "expand arrows alternate",
  basement: "level down alternate",
  garageSpaces: "car",
  lotSizeSqft: "map outline",
};

const OWNERSHIP_ICONS = { hoa: "building outline", metro_district: "university", tax: "dollar sign", amenities: "tree", insurance: "shield alternate" };

function KeyFindingRow({ k, onOpenTab }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pi-row">
      <div className={`pi-row-icon ${k.display.tone}`}><Icon name={THEME_ICONS[k.theme] || "info circle"} /></div>
      <div style={{ minWidth: 0 }}>
        <div className="pi-row-title">{k.title}</div>
        <div className="pi-row-text">{k.detail}</div>
      </div>
      <div className="pi-row-side">
        <StatusBadge display={k.display} />
        <Expander open={open} onClick={() => setOpen(!open)} label={k.title} />
      </div>
      {open && (
        <div className="pi-detail">
          {k.whyItMatters && <p><span className="pi-label">Why it matters: </span>{k.whyItMatters}</p>}
          {k.action && <p><span className="pi-label">Next step: </span>{k.action}</p>}
          <SourceLinks sources={k.sources} />
          {k.tab && TAB_TITLES[k.tab] && (
            <p style={{ marginTop: 8 }}>
              <button type="button" className="pi-link" onClick={() => onOpenTab(k.tab)}>
                See {TAB_TITLES[k.tab]} <Icon name="arrow right" />
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function OverviewTab({ p, onOpenTab }) {
  return (
    <div className="pi-stack">
      <div className="pi-grid-2">
        <div className="pi-col">
          <Card title="Executive Property Assessment" icon="clipboard outline">
            {p.executive.paragraphs.length === 0 ? <p className="pi-empty">The assessment will appear when the report is written.</p> : (
              p.executive.paragraphs.map((para, i) => <p key={i} className="pi-p">{para.text}</p>)
            )}
            <button type="button" className="pi-link" onClick={() => onOpenTab("fullReport")}>
              Read the full report <Icon name="arrow right" />
            </button>
          </Card>
          <Card title="Key Findings" icon="star outline" count={p.keyFindings.length}>
            {p.keyFindings.length === 0 ? <p className="pi-empty">No significant findings stood out in the research.</p> : (
              p.keyFindings.map((k) => <KeyFindingRow key={k.id} k={k} onOpenTab={onOpenTab} />)
            )}
          </Card>
        </div>

        <div className="pi-col">
          {p.glance.length > 0 && (
            <Card title="At a Glance" icon="home">
              <div className="pi-tiles">
                {p.glance.map((g) => (
                  <div key={g.key} className="pi-tile">
                    <Icon name={GLANCE_ICONS[g.key] || "info"} />
                    <div style={{ minWidth: 0 }}>
                      <div className="pi-tile-label">{g.label}</div>
                      <div className="pi-tile-value">
                        {g.display}
                        {g.conflict && <Icon name="exchange" color="orange" title="Sources disagree" style={{ marginLeft: 6 }} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10 }}>
                <button type="button" className="pi-link pi-small" onClick={() => onOpenTab("details")}>
                  Sources and verification <Icon name="arrow right" />
                </button>
              </div>
            </Card>
          )}

          {p.ownership.length > 0 && (
            <Card title="Ownership Considerations" icon="key">
              <table className="pi-kv">
                <tbody>
                  {p.ownership.map((o) => (
                    <tr key={o.key}>
                      <td className="k">
                        <Icon name={OWNERSHIP_ICONS[o.kind] || "info circle"} style={{ color: "#8a94a6" }} />
                        {o.label}
                      </td>
                      <td className="v">
                        <div style={{ fontWeight: o.value ? 600 : 400, color: o.value ? undefined : "#475467" }}>{o.title}</div>
                        {o.value && <div style={{ fontWeight: 700 }}>{o.value}</div>}
                        {o.note && <div className="pi-small pi-muted" style={{ fontWeight: 400 }}>{o.note}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8 }}>
                <button type="button" className="pi-link pi-small" onClick={() => onOpenTab("hoaMetro")}>
                  HOA, districts &amp; taxes <Icon name="arrow right" />
                </button>
              </div>
            </Card>
          )}

          <Card title="Recommended Next Steps" icon="tasks">
            {p.nextSteps.length === 0 ? <p className="pi-empty">No specific follow-up was recommended.</p> : (
              <ul className="pi-steps">
                {p.nextSteps.map((s) => (
                  <li key={`${s.index}-${s.action}`} className={s.status === "done" ? "done" : ""}>
                    <Icon name={s.status === "done" ? "check circle" : "check circle outline"} />
                    <div>
                      <div className="pi-step-title">{s.action}</div>
                      {s.reason && <div className="pi-step-why">{s.reason}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {p.verify.length > 0 && <VerifyCard items={p.verify} />}
    </div>
  );
}

export function VerifyCard({ items, title = "Additional Information to Verify" }) {
  if (!items?.length) return null;
  return (
    <Card title={title} icon="clipboard check" sub="Records that couldn't be confirmed online. A missing record isn't evidence of a problem - or of its absence.">
      <div className="pi-grid-even" style={{ gap: "4px 24px" }}>
        {items.map((v) => (
          <div key={v.key} style={{ padding: "6px 0" }}>
            <span className="pi-label">{v.label}: </span>
            <span>{v.text}</span>
            {v.link && <> <LinkOut link={v.link} /></>}
          </div>
        ))}
      </div>
    </Card>
  );
}

// -------------------------------------------------------- Property Details

function sourceChipClass(key) {
  if (key === "agent") return "agent";
  if (key === "parcel") return "county";
  if (key === "mls" || key === "datafiniti") return "mls";
  return "";
}

function FactOverrideForm({ fact, onEdit, onDone }) {
  const [value, setValue] = useState(fact.review?.value ?? "");
  const [note, setNote] = useState(fact.review?.note ?? "");
  const [saving, setSaving] = useState(false);
  async function save(clear) {
    setSaving(true);
    try {
      await onEdit({ type: "fact", field: fact.field, value: clear ? null : value, note });
      onDone();
    } finally {
      setSaving(false);
    }
  }
  return (
    <Form size="small" style={{ marginTop: 8 }}>
      <Form.Group widths="equal">
        <Form.Input label="Confirmed value" value={value} onChange={(e, { value: v }) => setValue(v)} />
        <Form.Input label="How you confirmed it" placeholder="e.g. assessor record, measured" value={note} onChange={(e, { value: v }) => setNote(v)} />
      </Form.Group>
      <Button size="mini" primary loading={saving} disabled={!String(value).trim()} onClick={() => save(false)} content="Save" />
      {fact.review && <Button size="mini" basic disabled={saving} onClick={() => save(true)} content="Remove confirmation" />}
      <Button size="mini" basic onClick={onDone} content="Cancel" />
    </Form>
  );
}

function FactRows({ rows, onEdit }) {
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  return (
    <table className="pi-kv">
      <tbody>
        {rows.map((f) => (
          <React.Fragment key={f.field}>
            <tr
              className="clickable"
              onClick={() => setOpen(open === f.field ? null : f.field)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen(open === f.field ? null : f.field))}
              tabIndex={0}
              aria-expanded={open === f.field}
            >
              <td className="k">{f.label}</td>
              <td className="v">
                {f.display}
                {f.conflict && <Icon name="exchange" color="orange" title="Sources disagree" style={{ marginLeft: 6 }} />}
                {f.review && <Icon name="user circle" color="blue" title="Confirmed by you" style={{ marginLeft: 6 }} />}
              </td>
              <td className="s"><span className={`pi-chip ${sourceChipClass(f.source.key)}`}>{f.source.label}</span></td>
            </tr>
            {open === f.field && (
              <tr>
                <td colSpan={3} style={{ paddingTop: 0 }}>
                  <div className="pi-detail" style={{ gridColumn: "auto" }}>
                    <p><span className="pi-label">Source: </span>{f.source.label}{f.review && f.originalDisplay !== f.display ? ` (${f.originalDisplay})` : ""}</p>
                    <p><span className="pi-label">Evidence: </span><StatusBadge display={f.evidence} /></p>
                    <p>
                      <span className="pi-label">Agent review: </span>
                      {f.review ? `Confirmed${f.review.note ? ` - ${f.review.note}` : ""}` : "Not reviewed"}
                    </p>
                    {f.otherValues?.length > 0 && <p><span className="pi-label">Other sources: </span>{f.otherValues.map((v) => `${v.source}: ${v.display}`).join("; ")}</p>}
                    <SourceLinks sources={f.sources} />
                    {onEdit && !f.sources && editing !== f.field && (
                      <button type="button" className="pi-link pi-small" onClick={() => setEditing(f.field)}>
                        {f.review ? "Edit your confirmation" : "Confirm or correct this value"}
                      </button>
                    )}
                    {editing === f.field && <FactOverrideForm fact={f} onEdit={onEdit} onDone={() => setEditing(null)} />}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

const FACT_GROUP_ICONS = { basic: "info circle", home: "home", living: "expand arrows alternate", systems: "plug", records: "file alternate outline" };

export function PropertyDetailsTab({ p, report, onEdit }) {
  const main = p.facts.filter((g) => !g.expandable);
  const records = p.facts.find((g) => g.expandable);
  const sqft = p.topics.squareFootage;
  const util = p.topics.utilities;
  return (
    <div className="pi-stack">
      <div className="pi-note">
        <Icon name="info circle" />
        <div>
          Each value shows its <strong>source</strong>. Only values that match an official county record are marked as independently
          verified - agent-entered and MLS values are labelled as such. Select a row for details or to confirm a value; the original records are kept.
        </div>
      </div>
      <div className="pi-grid-even">
        {main.map((g) => (
          <Card key={g.key} title={g.title} icon={FACT_GROUP_ICONS[g.key]}>
            <FactRows rows={g.rows} onEdit={onEdit} />
          </Card>
        ))}
        {records && (
          <Card title="Additional Property Records" icon="file alternate outline">
            <p className="pi-small pi-muted" style={{ marginTop: -4 }}>Legal description, parcel number and other record details.</p>
            <Disclosure label="Show record details" count={records.rows.length}>
              <FactRows rows={records.rows} onEdit={onEdit} />
            </Disclosure>
          </Card>
        )}
      </div>
      {sqft && <TopicCard topic={sqft} onEdit={onEdit} />}
      {util && <TopicCard topic={util} onEdit={onEdit} />}
      {report.inputSnapshot?.propertyInputs?.otherCharacteristics && (
        <Card title="Other characteristics you noted" icon="user">
          <p className="pi-p">{report.inputSnapshot.propertyInputs.otherCharacteristics}</p>
        </Card>
      )}
    </div>
  );
}

// -------------------------------------------------------- History & Permits

function formatDate(d) {
  if (!d) return "Date not stated";
  if (/^\d{4}$/.test(d)) return d;
  const t = Date.parse(d);
  if (!Number.isFinite(t)) return d;
  return new Date(t).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

export function HistoryPermitsTab({ p, onEdit }) {
  const h = p.history;
  const [showAll, setShowAll] = useState(false);
  const events = showAll ? h.events : h.events.filter((e) => h.primaryEventIds.includes(e.id));
  const hiddenCount = h.events.length - h.primaryEventIds.length;
  return (
    <div className="pi-stack">
      <div className="pi-grid-2">
        <Card title="Property History" icon="history">
          {h.events.length === 0 ? <p className="pi-empty">No dated sales or listings were found.</p> : (
            <ul className="pi-timeline">
              {events.map((e) => (
                <li key={e.id} className={e.significance < 3 ? "minor" : ""}>
                  <div className="pi-when">{formatDate(e.date)} · {e.kindLabel}</div>
                  <div className="pi-row-title">{e.title}</div>
                  {e.detail && <div className="pi-row-text">{e.detail}</div>}
                  <div className="pi-sources">
                    <span className={`pi-chip ${e.sourceKind.key === "official" ? "county" : ""}`}>{e.sourceKind.label}</span>
                    {e.sources[0]?.url && (
                      <a href={e.sources[0].url} target="_blank" rel="noopener noreferrer">{e.sources[0].label}<Icon name="external alternate" /></a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {hiddenCount > 0 && (
            <button type="button" className="pi-link" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show key events only" : `View all history (${h.events.length})`}
            </button>
          )}
        </Card>

        <div className="pi-col">
          {h.topics.map((t) => <TopicCard key={t.id} topic={t} onEdit={onEdit} />)}
          {h.topics.length === 0 && (
            <Card title="History Notes" icon="sticky note outline"><p className="pi-empty">No history concerns stood out.</p></Card>
          )}
        </div>
      </div>

      <Card title="Permits" icon="file alternate outline" count={h.permits.length || undefined}>
        {h.permits.length > 0 && (
          <table className="pi-table" style={{ marginBottom: 12 }}>
            <thead>
              <tr><th>Permit</th><th>Date</th><th>Description</th><th>Status</th><th>Source</th></tr>
            </thead>
            <tbody>
              {h.permits.map((pm) => (
                <tr key={pm.id}>
                  <td data-label="Permit"><strong>{pm.name}</strong>{pm.count > 1 ? <span className="pi-muted"> ({pm.count} records)</span> : null}</td>
                  <td data-label="Date">{formatDate(pm.date)}</td>
                  <td data-label="Description">{pm.description || "-"}</td>
                  <td data-label="Status">
                    {pm.status}
                    {pm.sourceKind.key !== "official" && pm.reportedStatus && <div className="pi-small pi-muted">Reported: {pm.reportedStatus}</div>}
                  </td>
                  <td data-label="Source">
                    <span className={`pi-chip ${pm.sourceKind.key === "official" ? "county" : ""}`}>{pm.sourceKind.label}</span>
                    {pm.sources[0]?.url && <div><a className="pi-link pi-small" href={pm.sources[0].url} target="_blank" rel="noopener noreferrer">{pm.sources[0].label}<Icon name="external alternate" /></a></div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="pi-note neutral">
          <Icon name="info circle" />
          <div>
            {h.permitNote}
            {h.permitPortal && <> <LinkOut link={h.permitPortal} /></>}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------- HOA & Metro

function EntityCard({ e }) {
  return (
    <Card title={e.name} icon={e.kind === "hoa" ? "building outline" : "university"} right={<span className="pi-badge info">{e.kindLabel}</span>}>
      <table className="pi-kv">
        <tbody>
          <tr>
            <td className="k">{e.kind === "hoa" ? "Dues" : "District fee"}</td>
            <td className="v">
              {e.fees.length === 0 ? <span className="pi-muted" style={{ fontWeight: 400 }}>Current fee not confirmed</span> : e.fees.map((f, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  {f.display} <span className="pi-muted" style={{ fontWeight: 400 }}>· {f.frequency}{f.year ? ` (${f.year})` : ""}</span>
                  <div className="pi-small pi-muted" style={{ fontWeight: 400 }}>
                    {f.sourceKind.label}{f.alsoReportedBy?.length ? `; also reported by ${f.alsoReportedBy.join(", ")}` : ""}
                  </div>
                </div>
              ))}
              {e.fees.length > 0 && e.feeNote && <div className="pi-small" style={{ color: "#b45309", fontWeight: 600 }}>{e.feeNote}</div>}
            </td>
          </tr>
          {e.fees.some((f) => f.annualDisplay) && (
            <tr>
              <td className="k">Annual equivalent</td>
              <td className="v">{[...new Set(e.fees.map((f) => f.annualDisplay).filter(Boolean))].join(" or ")}</td>
            </tr>
          )}
          {e.kind === "metro_district" && (
            <tr>
              <td className="k">Billing</td>
              <td className="v" style={{ fontWeight: 400 }}>
                {e.fees.length ? "Fee billed separately from property taxes" : "Any separate fee not confirmed"}
                {e.levies.length > 0 && <div>Mill levy ({e.levies.map((l) => l.text).join("; ")}) is <strong>included in the property tax bill</strong></div>}
              </td>
            </tr>
          )}
          {e.services.length > 0 && (
            <tr>
              <td className="k">Services</td>
              <td className="v" style={{ fontWeight: 400 }}>{e.services[0]}</td>
            </tr>
          )}
          {e.operator && (
            <tr>
              <td className="k">Management</td>
              <td className="v" style={{ fontWeight: 400 }}>{e.operator}</td>
            </tr>
          )}
        </tbody>
      </table>
      <SourceLinks sources={e.links} label="More information" />
    </Card>
  );
}

const AMENITY_ICONS = { pool: "tint", clubhouse: "home", fitness: "heartbeat", courts: "table tennis", playground: "child", trail: "map signs", park: "tree", other: "star outline" };

export function HoaMetroTab({ p, onEdit }) {
  const hm = p.hoaMetro;
  return (
    <div className="pi-stack">
      {hm.entities.length === 0 ? (
        <Card title="HOA & Districts" icon="building outline"><p className="pi-empty">No HOA or metro district was identified. Confirm with the seller and title commitment.</p></Card>
      ) : (
        <div className="pi-grid-even">{hm.entities.map((e) => <EntityCard key={e.id} e={e} />)}</div>
      )}

      <Card title="Property Taxes" icon="dollar sign">
        <table className="pi-kv">
          <tbody>
            <tr>
              <td className="k">{hm.taxes.year ? `${hm.taxes.year} property taxes` : "Annual property taxes"}</td>
              <td className="v">
                {hm.taxes.display}
                {hm.taxes.conflicting && <div className="pi-small" style={{ color: "#b45309" }}>Reported amounts differ - confirm with the county treasurer.</div>}
              </td>
            </tr>
          </tbody>
        </table>
        {hm.taxes.note && <div className="pi-note neutral" style={{ marginTop: 10 }}><Icon name="info circle" /><div>{hm.taxes.note}</div></div>}
        <div style={{ marginTop: 8 }}>{hm.taxes.link && <LinkOut link={hm.taxes.link} />}</div>
      </Card>

      {hm.amenities.length > 0 && (
        <Card title="Community Amenities" icon="tree" sub="Access for this specific property should be confirmed with the HOA or district.">
          <div className="pi-amenities">
            {hm.amenities.map((a) => (
              <div key={a.id} className="pi-amenity">
                <div className={`pi-amenity-art ${a.kind}`} aria-hidden="true"><Icon name={AMENITY_ICONS[a.kind] || "star outline"} /></div>
                <div style={{ minWidth: 0 }}>
                  <div className="pi-row-title">{a.name}</div>
                  {a.description && <div className="pi-row-text">{a.description}</div>}
                  <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span className={`pi-badge ${a.access === "included" ? "positive" : "neutral"}`}>{a.accessLabel}</span>
                    {a.link && <LinkOut link={a.link}>Details</LinkOut>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {hm.topics.length > 0 && (
        <>
          <div className="pi-section-heading">Research Notes</div>
          <div className="pi-grid-even">{hm.topics.map((t) => <TopicCard key={t.id} topic={t} onEdit={onEdit} />)}</div>
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------- Risks & Insurance

const RISK_ICONS = { foundation: "warning sign", drainage: "tint", wildfire: "fire", insurance: "shield alternate", other: "clipboard list" };

function RiskCard({ r, onEdit, findings }) {
  return (
    <div className="pi-card pi-risk">
      <div className={`pi-row-icon ${r.display.tone}`}><Icon name={RISK_ICONS[r.key] || "info circle"} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <h4>{r.title}</h4>
          {r.scope && <span className="pi-chip">{r.scope.label}</span>}
        </div>
        <div className="pi-headline">{r.headline}</div>
        {r.explanation && <p className="pi-p" style={{ marginBottom: 8 }}>{r.explanation}</p>}
        {r.guidance && (
          <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
            {r.guidance.map((g) => <li key={g} style={{ marginBottom: 4 }}>{g}</li>)}
          </ul>
        )}
        {r.concernNote && <div className="pi-note" style={{ marginBottom: 8 }}><Icon name="user" /><div>{r.concernNote}</div></div>}
        {r.whyItMatters && <p className="pi-small" style={{ margin: "0 0 4px" }}><span className="pi-label">Why it matters: </span>{r.whyItMatters}</p>}
        {r.verify && <p className="pi-small" style={{ margin: 0 }}><span className="pi-label">Verify: </span>{r.verify}</p>}
        <EvidenceDisclosure findings={findings} onEdit={onEdit} />
      </div>
      <div className="pi-risk-side">
        <StatusBadge display={r.display} />
        {r.links.map((l) => (
          <a key={l.id || l.url} href={l.url} target="_blank" rel="noopener noreferrer">{l.label} <Icon name="external alternate" style={{ margin: 0 }} /></a>
        ))}
      </div>
    </div>
  );
}

export function RisksInsuranceTab({ p, onEdit }) {
  return (
    <div className="pi-stack">
      <div className="pi-note neutral">
        <Icon name="info circle" />
        <div>Each item says whether it comes from <strong>regional mapping</strong>, <strong>parcel-level mapping</strong>, a <strong>documented record</strong> for this property, or an <strong>agent concern</strong> still to be verified. Mapped exposure is not the condition of the home.</div>
      </div>
      {p.risks.map((r) => <RiskCard key={r.key} r={r} onEdit={onEdit} findings={r.findingIds.map((id) => p.findings[id]).filter(Boolean)} />)}
    </div>
  );
}

// ------------------------------------------------------------ Neighborhood

const NEIGHBORHOOD_ICONS = { parks: "tree", transportation: "road", development: "building", schools: "graduation cap", other: "map outline" };

function NeighborhoodItem({ item }) {
  return (
    <div style={{ padding: "10px 0", borderTop: "1px solid #eef1f5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div className="pi-row-title">{item.title}</div>
        {item.display && item.display.tone !== "neutral" && <StatusBadge display={item.display} />}
      </div>
      {item.detail && <div className="pi-row-text">{item.detail}</div>}
      {item.location && <div className="pi-small pi-muted">{item.location}</div>}
      {item.link && <div style={{ marginTop: 4 }}><LinkOut link={item.link} /></div>}
    </div>
  );
}

export function NeighborhoodTab({ p }) {
  const n = p.neighborhood;
  const safety = n.safety;
  return (
    <div className="pi-stack">
      {n.sections.length === 0 && <Card><p className="pi-empty">Little property-specific neighborhood information was found.</p></Card>}
      <div className="pi-grid-even">
        {n.sections.map((s) => (
          <Card key={s.key} title={s.title} icon={NEIGHBORHOOD_ICONS[s.key]}>
            <div style={{ marginTop: -10 }}>
              {[...s.items, ...s.findings].map((item) => <NeighborhoodItem key={item.id} item={item} />)}
            </div>
          </Card>
        ))}
        {(safety.resources.length > 0 || safety.records.length > 0 || safety.findings.length > 0) && (
          <Card title="Public Safety & Records" icon="shield">
            <p className="pi-small pi-muted" style={{ marginTop: -4 }}>{safety.note}</p>
            {safety.records.map((r) => <NeighborhoodItem key={r.id} item={r} />)}
            {safety.findings.map((r) => <NeighborhoodItem key={r.id} item={r} />)}
            {safety.resources.length > 0 && (
              <div style={{ borderTop: "1px solid #eef1f5", paddingTop: 10 }}>
                <div className="pi-label" style={{ marginBottom: 4 }}>Official resources</div>
                {safety.resources.map((r) => (
                  <div key={r.id} style={{ marginBottom: 4 }}><LinkOut link={{ url: r.link.url, label: r.title }} /></div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
      <p className="pi-small pi-muted" style={{ margin: 0 }}>
        Distance to a road, rail line or airport is not a measurement of noise at this home. Walk and bike scores are not included.
      </p>
    </div>
  );
}
